import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../assets/css/common.css';
import DocumentBody from '../components/DocumentBody';
import SignaturePad from '../components/SignaturePad';
import { documentService } from '../services/documentService';
import { photoPermissionService } from '../services/photoPermissionService';
import { confidentialityService } from '../services/confidentialityService';
import { memberService } from '../services/memberService';

function DocumentResign() {
  const { key } = useParams();
  const navigate = useNavigate();
  const padRef = useRef(null);

  const [doc, setDoc] = useState(null);
  const [memberName, setMemberName] = useState('');
  const [isApproved, setIsApproved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setDoc(await documentService.getCurrent(key));
      } catch (err) {
        setError(err?.data?.error || '書面の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    const fetchMemberName = async () => {
      try {
        const memberData = await memberService.getRegistration();
        if (memberData?.formData?.name) {
          setMemberName(memberData.formData.name);
        }
      } catch (err) {
        console.error('氏名取得エラー:', err);
      }
    };

    fetchDocument();
    fetchMemberName();
  }, [key]);

  const handleSubmit = async () => {
    if (!doc) return;

    if (doc.requiresApproval && isApproved === null) {
      setError('承諾または非承諾を選択してください。');
      return;
    }
    if (padRef.current?.isEmpty()) {
      setError('署名を記入してください。');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const signatureData = padRef.current.toDataURL();

      if (key === 'photo_permission') {
        await photoPermissionService.updatePhotoPermission({
          isApproved,
          signatureData,
          documentVersionId: doc.versionId
        });
      } else {
        await confidentialityService.saveConfidentialityAgreement({
          signatureData,
          documentVersionId: doc.versionId
        });
      }

      setDone(true);
    } catch (err) {
      setError(err?.data?.error || '再署名の保存に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="container" style={{ padding: 24 }}><p className="text-muted">読み込み中...</p></div>;
  }

  if (!doc) {
    return (
      <div className="container" style={{ padding: 24 }}>
        <div className="alert alert-danger">{error || '書面が見つかりません。'}</div>
        <Link to="/consents" className="btn btn-secondary">同意・誓約に戻る</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
        <h1 className="text-primary text-center mb-4">{doc.title}</h1>
        <div className="alert alert-success">
          最新版（v{doc.version}）へのご署名を受け付けました。ご対応ありがとうございました。
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/consents" className="btn btn-primary">同意・誓約を確認する</Link>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>マイページに戻る</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <h1 className="text-primary text-center mb-4">{doc.title}</h1>

      {doc.needsResign ? (
        <div className="alert alert-warning">
          {doc.title}が改訂されました（v{doc.signedVersion || '—'} → v{doc.version}）。
          内容をご確認のうえ、あらためてご署名をお願いいたします。
          {doc.changeNote && <div className="mt-1"><strong>改訂内容:</strong> {doc.changeNote}</div>}
        </div>
      ) : (
        <div className="alert alert-info">
          現在の書面（v{doc.version}）です。内容をご確認のうえ、署名し直すことができます。
        </div>
      )}

      <DocumentBody body={doc.body} values={{ name: memberName }} className="mb-4" />

      {doc.requiresApproval && (
        <div className="mb-4">
          <h2 className="text-center">画像使用承諾</h2>
          <p>
            私は、株式会社村田ソフトウェアサービスとそのグループ会社が撮影した写真及び動画を、上記の目的と方法で使用されることを
          </p>
          <small className="text-muted d-block mb-2">※どちらを選んでも手続きは完了します</small>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label htmlFor="resign-approval-yes" style={{ cursor: 'pointer', padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="resign-approval"
                id="resign-approval-yes"
                value="yes"
                onChange={() => setIsApproved(true)}
              />
              承諾します
            </label>
            <label htmlFor="resign-approval-no" style={{ cursor: 'pointer', padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="resign-approval"
                id="resign-approval-no"
                value="no"
                onChange={() => setIsApproved(false)}
              />
              承諾しません
            </label>
          </div>
        </div>
      )}

      <div className="mb-4">
        <SignaturePad ref={padRef} className="shadow rounded" />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-grid mt-4">
        <button className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={submitting}>
          {submitting ? '送信中...' : doc.needsResign ? '同意して再署名する' : '同意して署名する'}
        </button>
      </div>

      <div className="mt-3">
        <Link to="/consents" className="btn btn-secondary">同意・誓約に戻る</Link>
      </div>
    </div>
  );
}

export default DocumentResign;
